import type {
  ConditionGroup,
  FinancialSnapshot,
  GroupSummaryKey,
  Predicate,
  SectionSummaryKey,
} from "./fnaBasicConfig";

export type SummaryMap = Record<GroupSummaryKey | SectionSummaryKey, number>;

export const computeSummaries = (data: FinancialSnapshot): SummaryMap => {
  const income = data.income ?? {};
  const expense = data.expense ?? {};
  const currentAssets = data.currentAssets ?? {};
  const nonCurrentAssets = data.nonCurrentAssets ?? {};
  const liabilities = data.liabilities ?? {};

  const totalIncome =
    (income.salaryCommissionBonus ?? 0) +
    (income.rentalIncome ?? 0) +
    (income.hasOtherIncome ? income.otherIncomeAmount ?? 0 : 0);

  const totalExpense =
    (expense.personalAndFamilyExpense ?? 0) +
    (expense.mortgageLoanRepayment ?? 0) +
    (expense.insuranceContribution ?? 0) +
    (expense.hasOtherExpense ? expense.otherExpenseAmount ?? 0 : 0);

  const totalCurrentAssets =
    (currentAssets.cashAndDeposits ?? 0) +
    (currentAssets.liquidInvestments ?? 0) +
    (currentAssets.hasOtherCurrentAssets ? currentAssets.otherCurrentAssetsAmount ?? 0 : 0);

  const totalNonCurrentAssets = (nonCurrentAssets.property ?? 0) + (nonCurrentAssets.otherNonCurrentAssets ?? 0);

  const totalLiabilities =
    (liabilities.mortgageLoan ?? 0) +
    (liabilities.personalCreditCardLoan ?? 0) +
    (liabilities.hasOtherLiabilities ? liabilities.otherLiabilityAmount ?? 0 : 0);

  const surplusDeficit = totalIncome - totalExpense;
  const netAssets = totalCurrentAssets + totalNonCurrentAssets - totalLiabilities;

  return {
    totalIncome,
    totalExpense,
    totalCurrentAssets,
    totalNonCurrentAssets,
    totalLiabilities,
    surplusDeficit,
    netAssets,
  } as SummaryMap;
};

export const evaluateConditions = (
  condition: ConditionGroup | ConditionGroup[] | undefined,
  draft: FinancialSnapshot
): boolean => {
  if (!condition) return true;
  const groups = Array.isArray(condition) ? condition : [condition];

  const isSectionPredicate = (
    predicate: Predicate
  ): predicate is Extract<Predicate, { section: keyof FinancialSnapshot }> =>
    "section" in predicate;

  return groups.some((group) => {
    const mode = group.mode ?? "all";
    const passes = group.predicates.map((predicate) => {
      if ("fn" in predicate && typeof predicate.fn === "function") {
        return predicate.fn(draft);
      }
      if (!isSectionPredicate(predicate)) return false;

      const sectionValue = draft[predicate.section];
      const value =
        sectionValue && typeof sectionValue === "object"
          ? (sectionValue as Record<string, unknown>)[predicate.key]
          : undefined;

      if (predicate.equals !== undefined) return value === predicate.equals;
      if (predicate.truthy !== undefined) return predicate.truthy ? Boolean(value) : !value;
      return Boolean(value);
    });
    return mode === "any" ? passes.some(Boolean) : passes.every(Boolean);
  });
};

export const formatMoney = (val?: number) =>
  val === undefined || val === null || Number.isNaN(val) ? "--" : val.toLocaleString();
